import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PolarAngleAxis, PolarGrid, PolarRadiusAxis, Radar, RadarChart, ResponsiveContainer, Tooltip } from 'recharts';
import { useFullscreen } from '../../layouts/StackedLayout/StackedLayout';
import StaticSummaryGroup, { type StaticSummaryGroupItem } from './shared/StaticSummaryGroup';
import { createRadialLabelTickRenderer, type TeamResilienceStatistic } from './shared/utils';

interface TeamResilienceResultProps {
    statistics: TeamResilienceStatistic;
    summaryFormation?: number[];
}

const TeamResilienceResult = ({ statistics, summaryFormation = [3] }: TeamResilienceResultProps) => {
    const { t } = useTranslation('assessmentResultManagementPage');
    const { isMobile } = useFullscreen();

    const summaryItems: StaticSummaryGroupItem[] = useMemo(
        () =>
            statistics.summaryCards.map(item => ({
                title: `${item.value.toFixed(1)}/${item.total}`,
                description: item.label,
            })),
        [statistics.summaryCards]
    );

    return (
        <div>
            <div className="mx-auto grid max-w-3xl gap-5">
                <div className="w-full">
                    <StaticSummaryGroup formation={summaryFormation} isMobile={isMobile} items={summaryItems} />
                </div>

                <div className="flex w-full flex-col items-center justify-center">
                    <div className={`w-full ${isMobile ? 'aspect-3/2 max-h-[38vh]' : 'aspect-16/10 max-h-[50vh]'}`}>
                        <ResponsiveContainer>
                            <RadarChart
                                outerRadius="75%"
                                data={statistics.radarData}
                                margin={{
                                    top: 20,
                                    right: isMobile ? 44 : 64,
                                    left: isMobile ? 44 : 64,
                                }}
                            >
                                <PolarGrid strokeDasharray="3 3" stroke="#b7b8b9" />
                                <PolarAngleAxis
                                    dataKey="shortLabel"
                                    tick={createRadialLabelTickRenderer(isMobile ? 12 : 16, {
                                        fontSize: 12,
                                        fill: 'var(--color-neutral-500)',
                                        fontWeight: 500,
                                    })}
                                    stroke="#2221212c"
                                />
                                <PolarRadiusAxis
                                    angle={90}
                                    domain={[0, statistics.domainMax]}
                                    tickCount={statistics.tickCount}
                                    tick={{
                                        fontSize: 11,
                                        fill: 'var(--color-neutral-500)',
                                        dy: 0,
                                    }}
                                    axisLine={{ stroke: '#dce1ebff' }}
                                    tickLine={{ stroke: '#f13bbbff' }}
                                    stroke="var(--color-slate-700)"
                                    tickFormatter={value => value.toString()}
                                    orientation="middle"
                                />
                                <Radar
                                    name="Your Score"
                                    dataKey="value"
                                    stroke="var(--color-teal-600)"
                                    fill="#1fac7d56"
                                    fillOpacity={0.2}
                                    strokeWidth={2.5}
                                    dot={true}
                                    activeDot={{ r: 5 }}
                                />
                                <Tooltip
                                    formatter={(value: string) => [value, t('details.teamResilience.yourScore')]}
                                    labelFormatter={label => `${t('details.teamResilience.subcategory')}: ${label}`}
                                    contentStyle={{
                                        borderRadius: '8px',
                                        backgroundColor: 'white',
                                        borderColor: '#c7c5c5ff',
                                        boxShadow: 'var(--shadow)',
                                        color: 'hsl(var(--foreground))',
                                        padding: '0.75rem 1rem',
                                    }}
                                />
                            </RadarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TeamResilienceResult;
